/**
 * Modular heads: graft the head of any rigged base model ("donor") onto any body. The donor is cut with a horizontal
 * plane just below its neck joint; both neck rings are measured by slicing, the donor head is scaled so its neck
 * slightly encloses the body's neck (the seam hides inside; collars/ruffs/hoods cover it further) and translated onto
 * the body's neck ring. The body's own head and upper neck are hidden. Skin: a smooth neck → head blend along the
 * body's neck/head bones. `compositeBody` swaps the head inside the Body record, so garments (hats, hoods, ruffs) are
 * measured on the new head.
 */
import type { Body } from './garments';
import type { Landmarks, Skeleton, V3 } from './rig';
import { evaluate, restPose } from './pose';

export interface Donor {
  pos: Float32Array; nrm: Float32Array; uv: Float32Array; indices: Uint32Array; lm: Landmarks;
  /** Face-space attribute per vertex (u, v, frontness, depth), computed in the donor's own frame. */
  aux: Float32Array;
  /** Secondary texture UV and local repair blend, independent of face coordinates. */
  repair?: Float32Array;
  owner?: Uint8Array;
  sk?: Skeleton | null;
  joints?: Uint8Array;
  weights?: Float32Array;
}

export interface Graft {
  pos: Float32Array; nrm: Float32Array; uv: Float32Array; indices: Uint32Array; aux: Float32Array; repair: Float32Array;
  joints: Uint8Array; weights: Float32Array; owner: Uint8Array;
  /** Body vertices hidden by the graft (the body's own head). */
  hidden: Uint8Array;
  /** Body cut at an exact plane, including interpolated boundary attributes. */
  base: Body & Pick<Donor, 'nrm' | 'uv' | 'aux'> & { repair: Float32Array };
  headTop: V3; head: V3;
  face?: Landmarks['face'];
  headRadius: number;
  earRPath: V3[]; earLPath: V3[];
}

/** Clip the body at the join rather than keeping or dropping whole boundary triangles. */
function clipBody(body: Body, source: Pick<Donor, 'nrm' | 'uv' | 'aux' | 'repair'>, height: number): Graft['base'] {
  interface Vertex { key: string; p: number[]; n: number[]; uv: number[]; aux: number[]; repair: number[]; skin: Map<number, number> }
  const vertices: Vertex[] = [], indices: number[] = [], remap = new Map<string, number>();
  const from = (i: number): Vertex => {
    const skin = new Map<number, number>();
    for (let k = 0; k < 4; k++) if (body.weights[i * 4 + k] > 0) skin.set(body.joints[i * 4 + k], body.weights[i * 4 + k]);
    return { key: `v${i}`, p: Array.from(body.pos.subarray(i * 3, i * 3 + 3)), n: Array.from(source.nrm.subarray(i * 3, i * 3 + 3)),
      uv: Array.from(source.uv.subarray(i * 2, i * 2 + 2)), aux: Array.from(source.aux.subarray(i * 4, i * 4 + 4)),
      repair: source.repair ? Array.from(source.repair.subarray(i * 3, i * 3 + 3)) : [0, 0, 0], skin };
  };
  const edge = (a: Vertex, b: Vertex): Vertex => {
    const t = (height - a.p[1]) / (b.p[1] - a.p[1]);
    const mix = (x: number[], y: number[]) => x.map((v, k) => v + (y[k] - v) * t);
    const skin = new Map<number, number>();
    for (const [bone, w] of a.skin) skin.set(bone, w * (1 - t));
    for (const [bone, w] of b.skin) skin.set(bone, (skin.get(bone) ?? 0) + w * t);
    return { key: [a.key, b.key].sort().join(':'), p: mix(a.p, b.p), n: mix(a.n, b.n), uv: mix(a.uv, b.uv), aux: mix(a.aux, b.aux), repair: mix(a.repair, b.repair), skin };
  };
  for (let i = 0; i < body.indices.length; i += 3) {
    const sourceIndices = [body.indices[i], body.indices[i + 1], body.indices[i + 2]];
    // Long old whiskers can droop below the geometric neck cut. Their head-owned
    // triangles must leave with the old head instead of floating by the collar.
    if (sourceIndices.every(v => ['head', 'ear'].includes(body.sk.bones[body.owner[v]].chain))) continue;
    const triangle = sourceIndices.map(from);
    const clip = !sourceIndices.every(v => body.sk.bones[body.owner[v]].chain === 'tail');
    const polygon: Vertex[] = [];
    if (!clip) polygon.push(...triangle);
    else for (let k = 0; k < 3; k++) {
      const a = triangle[k], b = triangle[(k + 1) % 3], ai = a.p[1] <= height, bi = b.p[1] <= height;
      if (ai) polygon.push(a);
      if (ai !== bi) polygon.push(edge(a, b));
    }
    const mapped = polygon.map(v => { let id = remap.get(v.key); if (id === undefined) { id = vertices.length; vertices.push(v); remap.set(v.key, id); } return id; });
    for (let k = 1; k + 1 < mapped.length; k++) indices.push(mapped[0], mapped[k], mapped[k + 1]);
  }
  const count = vertices.length;
  const pos = new Float32Array(count * 3), nrm = new Float32Array(count * 3), uv = new Float32Array(count * 2), aux = new Float32Array(count * 4);
  const repair = new Float32Array(count * 3);
  const joints = new Uint8Array(count * 4), weights = new Float32Array(count * 4), owner = new Uint8Array(count);
  vertices.forEach((v, i) => {
    pos.set(v.p, i * 3); const length = Math.hypot(...v.n) || 1; nrm.set(v.n.map(n => n / length), i * 3);
    uv.set(v.uv, i * 2); aux.set(v.aux, i * 4); repair.set(v.repair, i * 3);
    const top = [...v.skin].sort((a, b) => b[1] - a[1]).slice(0, 4), sum = top.reduce((s, x) => s + x[1], 0) || 1;
    top.forEach(([bone, w], k) => { joints[i * 4 + k] = bone; weights[i * 4 + k] = w / sum; }); owner[i] = joints[i * 4];
  });
  return { ...body, pos, nrm, uv, aux, repair, indices: new Uint32Array(indices), joints, weights, owner };
}

/** Mean radius and centre of the ring where triangles cross the plane y = h (only triangles near the axis). */
function neckRing(pos: Float32Array, idx: Uint32Array, h: number, axis: V3, maxR: number): { c: V3; r: number } {
  const pts: V3[] = [];
  for (let t = 0; t < idx.length; t += 3) {
    const v = [idx[t], idx[t + 1], idx[t + 2]];
    for (let e = 0; e < 3; e++) {
      const a = v[e], b = v[(e + 1) % 3];
      const ya = pos[a * 3 + 1] - h, yb = pos[b * 3 + 1] - h;
      if ((ya > 0) === (yb > 0)) continue;
      const k = ya / (ya - yb);
      const p: V3 = [pos[a * 3] + (pos[b * 3] - pos[a * 3]) * k, h, pos[a * 3 + 2] + (pos[b * 3 + 2] - pos[a * 3 + 2]) * k];
      if (Math.hypot(p[0] - axis[0], p[2] - axis[2]) < maxR) pts.push(p);
    }
  }
  if (!pts.length) return { c: [axis[0], h, axis[2]], r: 0.05 };
  const c: V3 = [pts.reduce((s, p) => s + p[0], 0) / pts.length, h, pts.reduce((s, p) => s + p[2], 0) / pts.length];
  const r = pts.reduce((s, p) => s + Math.hypot(p[0] - c[0], p[2] - c[2]), 0) / pts.length;
  return { c, r };
}

/** Donor ears use their own pivots, including a hare's folded ear, before the
 * head is attached to another skeleton. Recipient ear pivots fit its old skull. */
function morphDonorEars(donor: Donor, earScale: number): Donor {
  if (earScale === 1 || !donor.sk || !donor.joints || !donor.weights) return donor;
  const pose = restPose(donor.sk);
  for (let i = 0; i < donor.sk.bones.length; i++) if (donor.sk.bones[i].chain === 'ear') {
    pose.bones[i].length = earScale; pose.bones[i].thick = earScale;
  }
  const skin = evaluate(donor.sk, pose).skin, pos = new Float32Array(donor.pos.length);
  for (let i = 0; i < donor.pos.length / 3; i++) {
    const x = donor.pos[i * 3], y = donor.pos[i * 3 + 1], z = donor.pos[i * 3 + 2];
    for (let k = 0; k < 4; k++) {
      const weight = donor.weights[i * 4 + k]; if (!weight) continue;
      const offset = donor.joints[i * 4 + k] * 16;
      for (let axis = 0; axis < 3; axis++) pos[i * 3 + axis] += weight * (skin[offset + axis] * x + skin[offset + axis + 4] * y + skin[offset + axis + 8] * z + skin[offset + axis + 12]);
    }
  }
  return { ...donor, pos };
}

export function graftHead(body: Body, donor: Donor, bodyAttributes: Pick<Donor, 'nrm' | 'uv' | 'aux' | 'repair'>, earScale = 1): Graft {
  donor = morphDonorEars(donor, earScale);
  const yd = donor.lm.neck[1] - 0.012;
  const yb = body.lm.neck[1] - 0.012;
  const dRing = neckRing(donor.pos, donor.indices, yd + 0.008, donor.lm.neck, 0.12);
  const bRing = neckRing(body.pos, body.indices, yb + 0.008, body.lm.neck, 0.12);
  // neck ratio, softly limited so a pin-headed donor never becomes a balloon (and vice versa)
  const s = Math.max(0.7, Math.min(1.45, (bRing.r / Math.max(1e-3, dRing.r)) * 1.05));
  const map = (p: V3): V3 => {
    const y = yb + (p[1] - yd) * s, x = (p[0] - dRing.c[0]) * s, z = (p[2] - dRing.c[2]) * s;
    // Head size has a lower limit, but the seam must still fit a narrow neck.
    // Taper just the lower donor neck; leaving it at the head scale creates a
    // horizontal fur shelf below collars on hare-body / round-cat hybrids.
    const t = Math.max(0, Math.min(1, (y - yb - .008) / .055)), blend = t * t * (3 - 2 * t);
    const fit = Math.min(1, bRing.r * 1.035 / Math.max(1e-6, Math.hypot(x, z)));
    const radial = fit + (1 - fit) * blend;
    return [bRing.c[0] + x * radial, y, bRing.c[2] + z * radial];
  };

  // Clip crossing triangles instead of throwing them away. A short overlap below the
  // body's cut keeps an open neck from becoming visible when the head turns or grows.
  const cut = yd - 0.025;
  interface Vertex { key: string; p: V3; n: V3; uv: number[]; aux: number[]; repair: number[]; part?: 'head' | 'earR' | 'earL' }
  const vertices: Vertex[] = [], keep: number[] = [], remap = new Map<string, number>();
  const part = (i: number): Vertex['part'] => {
    const bone = donor.sk && donor.owner ? donor.sk.bones[donor.owner[i]] : undefined;
    return bone?.chain === 'head' ? 'head' : bone?.chain === 'ear' ? (bone.name.startsWith('earR') ? 'earR' : 'earL') : undefined;
  };
  const source = (i: number): Vertex => ({ key: `v${i}`, p: Array.from(donor.pos.subarray(i * 3, i * 3 + 3)) as V3,
    n: Array.from(donor.nrm.subarray(i * 3, i * 3 + 3)) as V3,
    uv: Array.from(donor.uv.subarray(i * 2, i * 2 + 2)), aux: Array.from(donor.aux.subarray(i * 4, i * 4 + 4)),
    repair: donor.repair ? Array.from(donor.repair.subarray(i * 3, i * 3 + 3)) : [0, 0, 0], part: part(i) });
  const between = (a: Vertex, b: Vertex): Vertex => {
    const t = (cut - a.p[1]) / (b.p[1] - a.p[1]);
    const lerp = (x: number[], y: number[]) => x.map((v, i) => v + (y[i] - v) * t);
    return { key: [a.key, b.key].sort().join(':'), p: lerp(a.p, b.p) as V3, n: lerp(a.n, b.n) as V3, uv: lerp(a.uv, b.uv), aux: lerp(a.aux, b.aux), repair: lerp(a.repair, b.repair), part: a.part === b.part ? a.part : undefined };
  };
  const nearHead = (i: number) => donor.owner && donor.sk ?
    donor.sk.bones[donor.owner[i]].chain !== 'tail' :
    Math.hypot(donor.pos[i * 3] - donor.lm.head[0], donor.pos[i * 3 + 2] - donor.lm.head[2]) < 0.32;
  for (let t = 0; t < donor.indices.length; t += 3) {
    const a = donor.indices[t], b = donor.indices[t + 1], c = donor.indices[t + 2];
    if (!nearHead(a) || !nearHead(b) || !nearHead(c)) continue;
    const triangle = [source(a), source(b), source(c)], clipped: Vertex[] = [];
    for (let e = 0; e < 3; e++) {
      const va = triangle[e], vb = triangle[(e + 1) % 3], ia = va.p[1] >= cut, ib = vb.p[1] >= cut;
      if (ia) clipped.push(va);
      if (ia !== ib) clipped.push(between(va, vb));
    }
    const mapped = clipped.map(v => { let id = remap.get(v.key); if (id === undefined) { id = vertices.length; vertices.push(v); remap.set(v.key, id); } return id; });
    for (let k = 1; k + 1 < mapped.length; k++) keep.push(mapped[0], mapped[k], mapped[k + 1]);
  }
  const n = vertices.length;
  const pos = new Float32Array(n * 3), nrm = new Float32Array(n * 3), uv = new Float32Array(n * 2), aux = new Float32Array(n * 4);
  const repair = new Float32Array(n * 3);
  for (let dst = 0; dst < n; dst++) {
    const vertex = vertices[dst], p = map(vertex.p);
    pos.set(p, dst * 3);
    const normalLength = Math.hypot(...vertex.n) || 1;
    nrm.set(vertex.n.map(v => v / normalLength), dst * 3);
    uv.set(vertex.uv, dst * 2);
    aux.set(vertex.aux, dst * 4);
    repair.set(vertex.repair, dst * 3);
  }
  // skin: neck → head blend between the cut and the body's head joint, head above
  const sk = body.sk;
  const neckB = sk.byName.get('neck')!, headB = sk.byName.get('head')!;
  const joints = new Uint8Array(n * 4), weights = new Float32Array(n * 4), owner = new Uint8Array(n);
  const hy = body.lm.head[1];
  const seamVertices: number[] = [];
  for (let i = 0; i < body.pos.length / 3; i++) {
    if (Math.abs(body.pos[i * 3 + 1] - yb) < 0.07 && Math.hypot(body.pos[i * 3] - bRing.c[0], body.pos[i * 3 + 2] - bRing.c[2]) < 0.16) seamVertices.push(i);
  }
  for (let i = 0; i < n; i++) {
    const y = pos[i * 3 + 1];
    const t = Math.max(0, Math.min(1, (y - yb - 0.025) / Math.max(1e-3, hy - yb - 0.025)));
    const k = t * t * (3 - 2 * t), merged = new Map<number, number>([[headB, k]]);
    if (k < 1) {
      // The base neck already contains chest/head blend weights. Using a fresh
      // neck-only blend here separates the two surfaces under proportion morphs.
      let nearest = -1, distance = Infinity;
      for (const v of seamVertices) {
        const d = (body.pos[v * 3] - pos[i * 3]) ** 2 + (body.pos[v * 3 + 1] - y) ** 2 + (body.pos[v * 3 + 2] - pos[i * 3 + 2]) ** 2;
        if (d < distance) { nearest = v; distance = d; }
      }
      if (nearest < 0) merged.set(neckB, 1 - k);
      else for (let j = 0; j < 4; j++) {
        const bone = body.joints[nearest * 4 + j], weight = body.weights[nearest * 4 + j] * (1 - k);
        merged.set(bone, (merged.get(bone) ?? 0) + weight);
      }
    }
    const top = [...merged].sort((a, b) => b[1] - a[1]).slice(0, 4), total = top.reduce((sum, pair) => sum + pair[1], 0);
    top.forEach(([bone, weight], j) => { joints[i * 4 + j] = bone; weights[i * 4 + j] = weight / total; });
    // Anatomical ownership drives garment coverage. A whisker or tooth can be
    // blended with the neck without becoming a surface of the neck garment.
    owner[i] = vertices[i].part ? sk.byName.get(vertices[i].part!) ?? headB : joints[i * 4];
  }
  // hide the body's own head and upper neck
  const vc = body.pos.length / 3;
  const hidden = new Uint8Array(vc);
  // Ownership is a skinning heuristic, not an anatomical cut: broad chest
  // capsules can own upper-neck vertices and leave old-head shards behind.
  for (let v = 0; v < vc; v++) if (body.pos[v * 3 + 1] > yb + 0.006 &&
    Math.hypot(body.pos[v * 3] - body.lm.head[0], body.pos[v * 3 + 2] - body.lm.head[2]) < 0.32) hidden[v] = 1;
  const headTop = map(donor.lm.headTop), head = map(donor.lm.head);
  const base = clipBody(body, bodyAttributes, yb + 0.006);
  const face = donor.lm.face ? { center: map(donor.lm.face.center), scale: donor.lm.face.scale * s } : undefined;
  // Garments need the donor's ear roots, not the recipient skeleton's old skull.
  // Keep bent ear paths as well as their tips, scaled around the native root.
  const earPath = (side: 'R' | 'L'): V3[] => {
    const tip = donor.lm[`ear${side}`], boneId = donor.sk?.byName.get(`ear${side}`);
    const nativeRoot = boneId === undefined ? undefined : donor.sk!.bones[boneId].head;
    const path = donor.lm[`ear${side}Path`] ?? (tip ? [nativeRoot ?? [tip[0] * .6 + donor.lm.head[0] * .4, tip[1] - .06, tip[2]] as V3, tip] : []);
    if (!path.length) return [];
    const root = path[0];
    return path.map(p => map(p.map((v, axis) => root[axis] + (v - root[axis]) * earScale) as V3));
  };
  return { pos, nrm, uv, aux, repair, indices: new Uint32Array(keep), joints, weights, owner, hidden, base, headTop, head, face,
    headRadius: donor.lm.radius.head * s, earRPath: earPath('R'), earLPath: earPath('L') };
}

/** The body with its head replaced by the graft (for measuring garments); indices of the graft are offset. */
export function compositeBody(body: Body, g: Graft): Body {
  body = g.base;
  const vc = body.pos.length / 3, gn = g.pos.length / 3;
  const pos = new Float32Array((vc + gn) * 3); pos.set(body.pos); pos.set(g.pos, vc * 3);
  const owner = new Uint8Array(vc + gn); owner.set(body.owner); owner.set(g.owner, vc);
  const joints = new Uint8Array((vc + gn) * 4); joints.set(body.joints); joints.set(g.joints, vc * 4);
  const weights = new Float32Array((vc + gn) * 4); weights.set(body.weights); weights.set(g.weights, vc * 4);
  const tris: number[] = [];
  for (let t = 0; t < body.indices.length; t += 3) {
    const a = body.indices[t], b = body.indices[t + 1], c = body.indices[t + 2];
    tris.push(a, b, c);
  }
  for (let t = 0; t < g.indices.length; t++) tris.push(g.indices[t] + vc);
  const lm: Landmarks = { ...body.lm, head: g.head, headTop: g.headTop, face: g.face, radius: { ...body.lm.radius, head: g.headRadius },
    earRPath: g.earRPath, earLPath: g.earLPath, earR: g.earRPath.at(-1), earL: g.earLPath.at(-1) };
  return { pos, indices: new Uint32Array(tris), owner, joints, weights, sk: body.sk, lm };
}
